import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { MapPin, Pencil, Trash2, Star, Plus, CreditCard } from 'lucide-react'
import {
  getAddresses, getSavedCards, createAddress, updateAddress, deleteAddress, setDefaultAddress,
  type AddressItem, type AddressFormData, type SavedCardItem,
} from '../../api/customerPayments'

const COUNTRY_OPTIONS = [
  { code: 'BA', label: 'country_ba' },
  { code: 'HR', label: 'country_hr' },
  { code: 'RS', label: 'country_rs' },
  { code: 'ME', label: 'country_me' },
  { code: 'SI', label: 'country_si' },
  { code: 'AT', label: 'country_at' },
  { code: 'DE', label: 'country_de' },
]

const emptyForm: AddressFormData = { full_name: '', address: '', city: '', zip: '', country: 'BA', phone: '' }

export default function AddressesPage() {
  const { t } = useTranslation('portal')
  const [addresses, setAddresses] = useState<AddressItem[]>([])
  const [cards, setCards] = useState<SavedCardItem[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [editingId, setEditingId] = useState<string | null>(null)
  const [showNewForm, setShowNewForm] = useState(false)
  const [form, setForm] = useState<AddressFormData>({ ...emptyForm })

  const load = async () => {
    setLoading(true)
    const [addrs, savedCards] = await Promise.all([getAddresses(), getSavedCards()])
    setAddresses(addrs)
    setCards(savedCards)
    setLoading(false)
  }

  useEffect(() => { load() }, [])

  const linkedCards = (addressId: string) =>
    cards.filter((c) => c.address_id === addressId)

  const isFormValid = () =>
    form.full_name.trim().length >= 2 &&
    form.address.trim().length >= 2 &&
    form.city.trim().length >= 2 &&
    form.zip.trim().length >= 2 &&
    form.phone.trim().length >= 3

  const handleSave = async () => {
    setError(null)
    if (!isFormValid()) return
    if (editingId) {
      const updated = await updateAddress(editingId, form)
      if (updated) {
        setAddresses((prev) => prev.map((a) => (a.id === editingId ? { ...a, ...updated } : a)))
        setEditingId(null)
      } else {
        setError(t('dashboard.addressSaveFailed', 'Failed to save address'))
      }
    } else {
      const created = await createAddress(form)
      if (created) {
        setAddresses((prev) => [...prev, created])
        setShowNewForm(false)
        setForm({ ...emptyForm })
      } else {
        setError(t('dashboard.addressSaveFailed', 'Failed to save address'))
      }
    }
  }

  const handleDelete = async (id: string) => {
    setError(null)
    const result = await deleteAddress(id)
    if (result.ok) {
      setAddresses((prev) => prev.filter((a) => a.id !== id))
    } else {
      setError(result.error || 'Failed to delete')
    }
  }

  const handleSetDefault = async (id: string) => {
    const ok = await setDefaultAddress(id)
    if (ok) {
      setAddresses((prev) => prev.map((a) => ({ ...a, is_default: a.id === id })))
    }
  }

  const startEdit = (addr: AddressItem) => {
    setEditingId(addr.id)
    setShowNewForm(false)
    setForm({
      full_name: addr.full_name,
      address: addr.address,
      city: addr.city,
      zip: addr.zip,
      country: addr.country,
      phone: addr.phone,
    })
  }

  const cancelEdit = () => {
    setEditingId(null)
    setShowNewForm(false)
    setForm({ ...emptyForm })
    setError(null)
  }

  const startNew = () => {
    setEditingId(null)
    setShowNewForm(true)
    setForm({ ...emptyForm })
    setError(null)
  }

  const countryName = (code: string) => {
    const opt = COUNTRY_OPTIONS.find((c) => c.code === code)
    return opt ? t(`payment.address.${opt.label}`, code) : code
  }

  const brandLabel = (brand: string) => {
    const b = (brand || '').toLowerCase()
    if (b.includes('visa')) return 'Visa'
    if (b.includes('master')) return 'Mastercard'
    if (b.includes('maestro')) return 'Maestro'
    return brand || 'Card'
  }

  const renderForm = () => (
    <div className="bg-white border border-slate-200 rounded-xl p-5 space-y-4">
      <h3 className="font-medium text-slate-900">
        {editingId ? t('dashboard.editAddress', 'Edit address') : t('dashboard.addAddress', 'Add new address')}
      </h3>
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-red-700 text-sm">{error}</div>
      )}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div className="sm:col-span-2">
          <input
            type="text" value={form.full_name}
            onChange={(e) => setForm((p) => ({ ...p, full_name: e.target.value }))}
            placeholder={t('payment.address.full_name', 'Full name')}
            className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            maxLength={100}
          />
        </div>
        <div className="sm:col-span-2">
          <input
            type="text" value={form.address}
            onChange={(e) => setForm((p) => ({ ...p, address: e.target.value }))}
            placeholder={t('payment.address.address', 'Street address')}
            className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            maxLength={255}
          />
        </div>
        <input
          type="text" value={form.city}
          onChange={(e) => setForm((p) => ({ ...p, city: e.target.value }))}
          placeholder={t('payment.address.city', 'City')}
          className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          maxLength={100}
        />
        <input
          type="text" value={form.zip}
          onChange={(e) => setForm((p) => ({ ...p, zip: e.target.value }))}
          placeholder={t('payment.address.zip', 'ZIP / Postal code')}
          className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          maxLength={20}
        />
        <select
          value={form.country}
          onChange={(e) => setForm((p) => ({ ...p, country: e.target.value }))}
          className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
        >
          {COUNTRY_OPTIONS.map((c) => (
            <option key={c.code} value={c.code}>{t(`payment.address.${c.label}`, c.code)}</option>
          ))}
        </select>
        <input
          type="tel" value={form.phone}
          onChange={(e) => setForm((p) => ({ ...p, phone: e.target.value }))}
          placeholder={t('payment.address.phone', 'Phone')}
          className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          maxLength={50}
        />
      </div>
      <div className="flex gap-2 justify-end">
        <button type="button" onClick={cancelEdit}
          className="px-4 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded-lg">
          {t('dashboard.cancel', 'Cancel')}
        </button>
        <button type="button" onClick={handleSave} disabled={!isFormValid()}
          className="px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50">
          {t('dashboard.save', 'Save')}
        </button>
      </div>
    </div>
  )

  if (loading) {
    return (
      <div>
        <h1 className="text-2xl font-bold text-slate-900 mb-6">{t('dashboard.myAddresses', 'My Addresses')}</h1>
        <p className="text-slate-500">{t('dashboard.loading', 'Loading...')}</p>
      </div>
    )
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-slate-900">{t('dashboard.myAddresses', 'My Addresses')}</h1>
        {!showNewForm && !editingId && (
          <button type="button" onClick={startNew}
            className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm">
            <Plus className="w-4 h-4" />
            {t('dashboard.addAddress', 'Add new address')}
          </button>
        )}
      </div>

      {error && !showNewForm && !editingId && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-red-700 text-sm mb-4">{error}</div>
      )}

      {showNewForm && renderForm()}

      {addresses.length === 0 && !showNewForm ? (
        <div className="bg-white rounded-xl border border-slate-200 p-8 text-center">
          <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <MapPin className="w-8 h-8 text-slate-500" />
          </div>
          <p className="text-slate-600 mb-4">{t('dashboard.noAddresses', 'No saved addresses.')}</p>
          <button type="button" onClick={startNew}
            className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">
            <Plus className="w-4 h-4" />
            {t('dashboard.addAddress', 'Add new address')}
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          {addresses.map((addr) => {
            const linked = linkedCards(addr.id)
            const isEditing = editingId === addr.id
            if (isEditing) return <div key={addr.id}>{renderForm()}</div>
            return (
              <div key={addr.id} className="bg-white border border-slate-200 rounded-xl p-4">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-medium text-slate-900">{addr.full_name}</span>
                      {addr.is_default && (
                        <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full">
                          {t('payment.address.default_label', 'default')}
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-slate-600">{addr.address}</p>
                    <p className="text-sm text-slate-600">{addr.city} {addr.zip}, {countryName(addr.country)}</p>
                    <p className="text-sm text-slate-500">{addr.phone}</p>
                    {linked.length > 0 && (
                      <div className="flex items-center gap-1 mt-2 text-xs text-slate-500">
                        <CreditCard className="w-3 h-3" />
                        <span>
                          {t('dashboard.linkedCards', '{{count}} card(s) use this address', { count: linked.length })}:
                          {' '}{linked.map((c) => `${brandLabel(c.card_brand)} ····${c.last4}`).join(', ')}
                        </span>
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-1 ml-4">
                    {!addr.is_default && (
                      <button type="button" onClick={() => handleSetDefault(addr.id)}
                        title={t('dashboard.setDefault', 'Set as default')}
                        className="p-2 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition">
                        <Star className="w-4 h-4" />
                      </button>
                    )}
                    <button type="button" onClick={() => startEdit(addr)}
                      title={t('dashboard.edit', 'Edit')}
                      className="p-2 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition">
                      <Pencil className="w-4 h-4" />
                    </button>
                    <button type="button"
                      onClick={() => linked.length === 0 && handleDelete(addr.id)}
                      disabled={linked.length > 0}
                      title={linked.length > 0
                        ? t('dashboard.cannotDeleteLinked', 'Cannot delete: cards use this address')
                        : t('dashboard.delete', 'Delete')}
                      className={`p-2 rounded-lg transition ${
                        linked.length > 0
                          ? 'text-slate-300 cursor-not-allowed'
                          : 'text-slate-400 hover:text-red-600 hover:bg-red-50'
                      }`}>
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}
